import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  ArrowLeft, Check, X, ChevronLeft, ChevronRight,
  Users, Clock, Target, BookOpen, FileDown, Unlock, Download
} from "lucide-react";

// Helper to format percentage with 2 decimal places
const formatPercent = (value) => {
  if (value === null || value === undefined) return '0.00';
  return Number(value).toFixed(2);
};

const InstructorExamReviewPage = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [students, setStudents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedStudentId, setSelectedStudentId] = useState("all");

  useEffect(() => {
    fetchReviewData();
  }, [examId]);

  const fetchReviewData = async () => {
    try {
      const response = await axios.get(
        `${API}/instructor/exams/${examId}/full-review`,
        { withCredentials: true }
      );
      setExam(response.data.exam);
      setQuestions(response.data.questions);
      setStudents(response.data.students);
    } catch (error) {
      console.error("Error fetching review:", error);
      toast.error("Erro ao carregar revisão do exame");
      navigate("/instructor");
    } finally {
      setLoading(false);
    }
  };

  const unlockAllReviews = async () => {
    try {
      await axios.post(
        `${API}/instructor/exams/${examId}/unlock-all`,
        {},
        { withCredentials: true }
      );
      toast.success("Revisões desbloqueadas para todos os alunos");
    } catch (error) {
      toast.error("Erro ao desbloquear revisões");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cockpit-bg flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  // Calculate stats for current question
  const getAnswerStats = (answerId) => {
    if (selectedStudentId !== "all") {
      const student = students.find(s => s.student_id === selectedStudentId);
      return student?.answers?.[currentQuestion.question_id] === answerId ? 1 : 0;
    }
    return students.filter(s => s.answers?.[currentQuestion.question_id] === answerId).length;
  };

  const totalResponses = selectedStudentId === "all" 
    ? students.filter(s => s.answers?.[currentQuestion.question_id]).length
    : (students.find(s => s.student_id === selectedStudentId)?.answers?.[currentQuestion.question_id] ? 1 : 0);

  const correctCount = students.filter(s => 
    s.answers?.[currentQuestion.question_id] === currentQuestion.correct_answer
  ).length;

  return (
    <div className="min-h-screen bg-cockpit-bg">
      {/* Header */}
      <header className="bg-cockpit-panel border-b border-cockpit-border p-3 md:p-4">
        <div className="max-w-6xl mx-auto flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
            <Button
              variant="ghost"
              onClick={() => navigate("/instructor")}
              className="text-slate-400 hover:text-white shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-chivo text-base md:text-xl text-cockpit-text-bright truncate">{exam?.title}</h1>
              <p className="text-xs md:text-sm text-slate-400">Revisão · {questions.length} questões</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => window.open(`${API}/instructor/exams/${examId}/pdf`, '_blank')}
              className="btn-secondary-cockpit"
              title="Exportar resumo do exame"
            >
              <FileDown className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">PDF Resumo</span>
              <span className="sm:hidden">Resumo</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`${API}/instructor/exams/${examId}/pdf-complete`, '_blank')}
              className="btn-secondary-cockpit"
              title="Exportar resumo + todos os exames dos alunos"
            >
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">PDF Completo</span>
              <span className="sm:hidden">Completo</span>
            </Button>
            <Button
              variant="outline"
              onClick={unlockAllReviews}
              className="btn-secondary-cockpit"
            >
              <Unlock className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Desbloquear Todos</span>
              <span className="sm:hidden">Desbloquear</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-3 md:p-6 pb-24">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <Card className="instrument-panel">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-slate-400" />
              <div>
                <div className="text-2xl font-mono text-cockpit-text-bright">{students.length}</div>
                <div className="text-xs text-slate-500">Alunos</div>
              </div>
            </CardContent>
          </Card>
          <Card className="instrument-panel">
            <CardContent className="p-4 flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-orange-400" />
              <div>
                <div className="text-2xl font-mono text-cockpit-text-bright">{questions.length}</div>
                <div className="text-xs text-slate-500">Questões</div>
              </div>
            </CardContent>
          </Card>
          <Card className="instrument-panel">
            <CardContent className="p-4 flex items-center gap-3">
              <Target className="w-8 h-8 text-emerald-400" />
              <div>
                <div className="text-2xl font-mono text-cockpit-text-bright">
                  {formatPercent(students.length > 0 ? students.filter(s => s.passed).length / students.length * 100 : 0)}%
                </div>
                <div className="text-xs text-slate-500">Aprovação</div>
              </div>
            </CardContent>
          </Card>
          <Card className="instrument-panel">
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-400" />
              <div>
                <div className="text-2xl font-mono text-cockpit-text-bright">{exam?.duration_minutes}</div>
                <div className="text-xs text-slate-500">Minutos</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Student Filter */}
        <div className="mb-6">
          <Select value={selectedStudentId} onValueChange={setSelectedStudentId}>
            <SelectTrigger className="input-cockpit w-full sm:w-64">
              <SelectValue placeholder="Filtrar por aluno" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">Todos os alunos</SelectItem>
              {students.map(student => (
                <SelectItem key={student.student_id} value={student.student_id}>
                  {student.name} ({formatPercent(student.score)}%)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Question Card */}
        {currentQuestion && (
          <Card className="instrument-panel mb-6">
            <CardHeader className="border-b border-cockpit-border">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <CardTitle className="font-chivo text-base md:text-lg">
                    Questão {currentIndex + 1} de {questions.length}
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1 break-words">
                    {currentQuestion.section_name} · {currentQuestion.question_id}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-base md:text-lg font-mono whitespace-nowrap ${correctCount === students.length ? 'text-emerald-400' : correctCount === 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                    {correctCount}/{students.length} corretas
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatPercent(students.length > 0 ? correctCount / students.length * 100 : 0)}% acerto
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              {/* Question Statement */}
              <div className="mb-6">
                <p className="text-cockpit-text-bright text-base md:text-lg leading-relaxed break-words">
                  {currentQuestion.statement}
                </p>
                {currentQuestion.image && (
                  <img 
                    src={currentQuestion.image} 
                    alt="Question" 
                    className="mt-4 max-w-full rounded-lg"
                  />
                )}
              </div>

              {/* Answers */}
              <div className="space-y-3">
                {currentQuestion.answers?.map((answer, idx) => {
                  const answerId = answer.id || `ans_${idx}`;
                  const isCorrect = answerId === currentQuestion.correct_answer;
                  const responseCount = getAnswerStats(answerId);
                  const percentage = totalResponses > 0 ? (responseCount / totalResponses * 100) : 0;
                  
                  return (
                    <div 
                      key={idx}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isCorrect 
                          ? 'border-emerald-500 bg-emerald-500/10' 
                          : responseCount > 0 
                            ? 'border-red-500/50 bg-red-500/5' 
                            : 'border-cockpit-border bg-cockpit-bg'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono shrink-0 ${
                          isCorrect ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'
                        }`}>
                          {letters[idx]}
                        </div>
                        <div className="flex-1">
                          <p className={`${isCorrect ? 'text-emerald-400' : 'text-cockpit-text'}`}>
                            {answer.text}
                          </p>
                          {/* Response bar */}
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all ${isCorrect ? 'bg-emerald-500' : 'bg-red-500'}`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className={`text-xs font-mono w-20 text-right ${isCorrect ? 'text-emerald-400' : responseCount > 0 ? 'text-red-400' : 'text-slate-500'}`}>
                              {responseCount} ({formatPercent(percentage)}%)
                            </span>
                          </div>
                        </div>
                        {isCorrect && <Check className="w-5 h-5 text-emerald-400 shrink-0" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Explanation */}
              {currentQuestion.note && (
                <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-xs text-blue-400 uppercase tracking-wider mb-2">Explicação</p>
                  <p className="text-slate-300">{currentQuestion.note}</p>
                </div>
              )}

              {/* Who got it right */}
              {selectedStudentId === "all" && correctCount > 0 && (
                <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-xs text-emerald-400 uppercase tracking-wider mb-2">Respostas Corretas ({correctCount})</p>
                  <div className="flex flex-wrap gap-2">
                    {students
                      .filter(s => s.answers?.[currentQuestion.question_id] === currentQuestion.correct_answer)
                      .map(s => (
                        <span key={s.student_id} className="px-2 py-1 bg-emerald-500/20 rounded text-xs text-emerald-300">
                          {s.name}
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}

              {/* Who got it wrong */}
              {selectedStudentId === "all" && correctCount < students.length && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-xs text-red-400 uppercase tracking-wider mb-2">Respostas Erradas ({students.length - correctCount})</p>
                  <div className="flex flex-wrap gap-2">
                    {students
                      .filter(s => s.answers?.[currentQuestion.question_id] && s.answers?.[currentQuestion.question_id] !== currentQuestion.correct_answer)
                      .map(s => (
                        <span key={s.student_id} className="px-2 py-1 bg-red-500/20 rounded text-xs text-red-300">
                          {s.name}
                        </span>
                      ))
                    }
                    {students
                      .filter(s => !s.answers?.[currentQuestion.question_id])
                      .map(s => (
                        <span key={s.student_id} className="px-2 py-1 bg-slate-500/20 rounded text-xs text-slate-400">
                          {s.name} (não respondeu)
                        </span>
                      ))
                    }
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation — fixed bar at bottom */}
        <div className="fixed bottom-0 left-0 right-0 z-50">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to top, rgb(15,23,42) 0%, transparent 100%)" }} />
          <div className="relative bg-slate-900/95 backdrop-blur-xl border-t border-slate-700/50 shadow-2xl shadow-black/50">
            <div className="max-w-6xl mx-auto px-3 md:px-6 py-3 flex items-center justify-between gap-2 md:gap-4">
              <Button
                variant="outline"
                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                disabled={currentIndex === 0}
                className="btn-secondary-cockpit shrink-0"
              >
                <ChevronLeft className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">Anterior</span>
              </Button>

              {/* Smart pagination */}
              <div className="flex items-center gap-1 overflow-x-auto py-1 px-1 flex-1 justify-center">
                {questions.length <= 15 ? (
                  questions.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-8 h-8 rounded text-xs font-mono transition-colors shrink-0 ${
                        idx === currentIndex
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))
                ) : (
                  <>
                    <button
                      onClick={() => setCurrentIndex(0)}
                      className={`w-8 h-8 rounded text-xs font-mono transition-colors shrink-0 ${currentIndex === 0 ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                    >1</button>
                    {currentIndex > 2 && <span className="text-slate-500 px-1">...</span>}
                    {Array.from({ length: 3 }, (_, i) => currentIndex - 1 + i)
                      .filter(idx => idx > 0 && idx < questions.length - 1)
                      .map(idx => (
                        <button
                          key={idx}
                          onClick={() => setCurrentIndex(idx)}
                          className={`w-8 h-8 rounded text-xs font-mono transition-colors shrink-0 ${idx === currentIndex ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                        >{idx + 1}</button>
                      ))
                    }
                    {currentIndex < questions.length - 3 && <span className="text-slate-500 px-1">...</span>}
                    <button
                      onClick={() => setCurrentIndex(questions.length - 1)}
                      className={`w-8 h-8 rounded text-xs font-mono transition-colors shrink-0 ${currentIndex === questions.length - 1 ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                    >{questions.length}</button>
                  </>
                )}
              </div>

              <span className="text-sm text-slate-400 font-mono shrink-0 hidden sm:block">
                {currentIndex + 1}/{questions.length}
              </span>

              <Button
                variant="outline"
                onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
                disabled={currentIndex === questions.length - 1}
                className="btn-secondary-cockpit shrink-0"
              >
                <span className="hidden sm:inline">Próxima</span>
                <ChevronRight className="w-5 h-5 sm:ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InstructorExamReviewPage;
